import cn from '../utils/cn.ts';
import { ContentDisplayStyle, useMapContext, ViewType } from './Map.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faCircleInfo,
  faMapSigns,
} from '@fortawesome/free-solid-svg-icons';
import Content from './Content.tsx';
import PanelContentFooter from './PanelContentFooter.tsx';
import { ReactNode, useContext } from 'react';
import { DataLayerStory, DataLayerType } from '../api/api.ts';
import NeumorphBox, { NeumorphType } from '@/common/ui/NeumorphBox.tsx';
import classNames from 'classnames';
import { LanguageContext } from '@/common/Language.tsx';

export default function PanelContent({
  plotPanelLarge,
}: {
  plotPanelLarge?: boolean;
}) {
  const {
    view,
    colorForSubcategoryId,
    setContentDisplayStyle,
    storyNavigateJump,
  } = useMapContext();
  const { t } = useContext(LanguageContext);

  return (
    <div
      className={cn(
        'absolute',
        'left-1/2',
        'top-[30px]',
        '-translate-x-1/2',
        'shadow-lg',
        'rounded-[12px]',
        'z-1000',
        'text-sm',
        'bg-gray-100',
        'transition-[width]',
        'transition-[height]',
        'py-[12px]',
        'px-[40px]',
        'overflow-y-hidden',
        'transition-all',
        plotPanelLarge === undefined
          ? 'max-h-[calc(100vh_-_2_*_30px)]'
          : plotPanelLarge
            ? 'max-h-[calc(100vh_-_3_*_30px_-_350px)]'
            : 'max-h-[calc(100vh_-_3_*_30px_-_250px)]',
        'w-[calc(_min(_600px,_100vw_-_(_4_*_30px_)_-_(_2_*_270px_)_)_)]',
        view && view.type === ViewType.Story ? '-translate-y-[100vh]' : null,
        'flex',
        'flex-col',
        'justify-between'
      )}
      onClick={() => {
        if (
          view &&
          view.type === ViewType.Regular &&
          view.contentStyle === ContentDisplayStyle.Hidden
        ) {
          setContentDisplayStyle(ContentDisplayStyle.Short);
        }
      }}
    >
      {(!view || view.type === ViewType.Story) && (
        <div className={cn('text-center')}>
          {t('Click on the', 'Klicken Sie auf das')}{' '}
          <FontAwesomeIcon icon={faCircleInfo} />{' '}
          {t(
            'in the project bar on the right to see a project description here.',
            'in der Projektleiste rechts um hier eine Projektbeschreibung einzublenden.'
          )}
        </div>
      )}
      {view && view.type === ViewType.Regular && (
        <>
          {view.contentStyle === ContentDisplayStyle.Hidden && (
            <PanelContentHeading
              title={t(view.project.name, view.project.nameDe)}
              oneLine={true}
              button={{
                label: (
                  <>
                    {t('READ', 'LESEN')}&nbsp;
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={cn('ml-[6px]')}
                    />
                  </>
                ),
                callback: () => {
                  setContentDisplayStyle(ContentDisplayStyle.Short);
                },
              }}
            />
          )}
          {view.contentStyle !== ContentDisplayStyle.Hidden && (
            <>
              <PanelContentHeading
                title={t(view.project.name, view.project.nameDe)}
                button={{
                  label: (
                    <>
                      {t('HIDE', 'VERSTECKEN')}&nbsp;
                      <FontAwesomeIcon
                        icon={faChevronUp}
                        className={cn('ml-[6px]')}
                      />
                    </>
                  ),
                  callback: () => {
                    setContentDisplayStyle(ContentDisplayStyle.Hidden);
                  },
                }}
                innerButton={
                  view.project.dataLayers[0].type === DataLayerType.Story
                    ? {
                        label: (
                          <>
                            {t('STORY', 'STORY')}&nbsp;
                            <FontAwesomeIcon
                              icon={faMapSigns}
                              className={cn('ml-[6px]')}
                            />
                          </>
                        ),
                        callback: () => {
                          storyNavigateJump(
                            view.project,
                            view.project.dataLayers[0] as DataLayerStory,
                            0
                          );
                        },
                      }
                    : undefined
                }
              />
              <div
                className={cn(
                  'flex-1',
                  '-mx-[40px]',
                  'overflow-y-auto',
                  'py-[12px]',
                  'px-[40px]'
                )}
              >
                <Content
                  project={view.project}
                  hideHeading={true}
                  content={
                    view.contentStyle === ContentDisplayStyle.Short
                      ? t(
                          view.project.shortContent,
                          view.project.shortContentDe
                        )
                      : t(view.project.content, view.project.contentDe)
                  }
                  doiLink={
                    view.contentStyle === ContentDisplayStyle.Full
                      ? view.project.doiLink ?? undefined
                      : undefined
                  }
                  doiLabel={
                    view.contentStyle === ContentDisplayStyle.Full
                      ? view.project.doiLabel ?? undefined
                      : undefined
                  }
                  color={colorForSubcategoryId(view.project.subcategoryId)}
                />
                {view.project.dataLayers[0].type === DataLayerType.Story && (
                  <div className={cn('text-center')}>
                    <NeumorphBox
                      className={classNames()}
                      inline={true}
                      type={NeumorphType.Hoverable}
                      onClick={() => {
                        storyNavigateJump(
                          view.project,
                          view.project.dataLayers[0] as DataLayerStory, // todo fix datalayerstory and datalayerplain to one layer
                          0
                        );
                      }}
                    >
                      {t('STORY MODE', 'STORY MODUS')}
                    </NeumorphBox>
                  </div>
                )}
              </div>
            </>
          )}
          <PanelContentFooter />
        </>
      )}
    </div>
  );
}

function PanelContentHeading({
  title,
  button,
  innerButton,
  oneLine,
}: {
  title: string;
  button?: { label: ReactNode; callback: () => void };
  innerButton?: { label: ReactNode; callback: () => void };
  oneLine?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex-[0_0_auto]',
        'flex',
        'items-stretch',
        oneLine ? '-my-[12px]' : '-mt-[12px]',
        '-mx-[40px]',
        oneLine && button
          ? [
              'hover:bg-radial',
              'hover:from-gray-100',
              'hover:to-gray-200',
              'cursor-pointer',
            ]
          : null
      )}
      onClick={() => {
        if (button && oneLine) {
          button.callback();
        }
      }}
    >
      <h3
        className={cn(
          'text-[1.4rem]',
          'font-medium',
          'flex-1',
          'mb-0',
          'p-[12px]',
          'pl-[40px]',
          button ? ['border-r-1', 'border-r-gray-200'] : null
          // bold || true ? 'font-bold' : null
        )}
      >
        {title}
      </h3>
      {button && !oneLine && innerButton && (
        <div
          className={cn(
            'flex-0',
            'p-[12px]',
            'border-r-1',
            'border-r-gray-200',
            'border-l-1',
            'border-l-white',
            'border-r-1',
            'border-r-gray-200',

            !oneLine && button && innerButton
              ? [
                  'bg-gray-200',
                  'hover:bg-radial',
                  'hover:from-gray-100',
                  'hover:to-gray-200',
                  'cursor-pointer',
                ]
              : null,
            'flex',
            'flex-col',
            'justify-center'
          )}
          onClick={() => {
            innerButton.callback();
          }}
        >
          <span>{innerButton.label}</span>
        </div>
      )}
      {button && (
        <div
          className={cn(
            'flex-0',
            'p-[12px]',
            'border-l-1',
            'border-l-white',
            'bg-gray-200',
            'hover:bg-radial',
            'hover:from-gray-100',
            'hover:to-gray-200',
            'cursor-pointer',

            !oneLine ? [] : null,
            'flex',
            'flex-col',
            'justify-center'
          )}
          onClick={() => {
            if (button && !oneLine) {
              button.callback();
            }
          }}
        >
          <span>{button.label}</span>
        </div>
      )}
    </div>
  );
}
